import {
  formatSvNumber,
  formatTgHdl,
  formatWhtr,
  type LowBmiAssessment,
  type LowBmiConditionNote,
  type LowBmiMeasures,
} from "@care-by-ella/contracts";

// TES-1116 — the doctor's lines for F-02's low-BMI section: WHtR with the
// measures behind it, then the patient's other answers, each with its date
// and source where she gave one. Pure, so the wording is tested without a
// DOM. The indicator labels come from contracts (A-02's wording); these
// lines only present values.

type Line = { label: string; value: string };

// Her answer about earlier results, under the label "Tidigare provsvar".
// "inga_provsvar" was "Jag har inga provsvar" in v13/v14 and is a bare "Nej"
// to "Har du tillgång till provsvaret?" from v16 (TES-1163); it also stands
// for "Nej" to having given blood samples at all. One neutral wording covers
// all of them without quoting a question she was no longer asked.
const LAB_STATUS: Record<string, string> = {
  ja: "Ja",
  inga_provsvar: "Inget tillgängligt provsvar",
  vet_inte: "Jag vet inte",
};
const HEREDITY: Record<string, string> = {
  tidig_hjart_karl:
    "Hjärtinfarkt eller stroke före 55 års ålder (män) eller före 65 års ålder (kvinnor)",
  typ2_diabetes: "Typ 2-diabetes",
  metabolt_syndrom: "Metabolt syndrom",
  arftlig_hyperlipidemi: "Ärftligt höga blodfetter eller högt kolesterol",
  inget: "Inget av detta",
  vet_inte: "Jag vet inte",
};
const YES_NO_UNSURE: Record<string, string> = {
  ja: "Ja",
  nej: "Nej",
  osaker: "Osäker",
};
const FREQUENCY: Record<string, string> = {
  sallan: "Sällan eller aldrig",
  nagon_gang_i_veckan: "Någon gång i veckan",
  flera_ganger_i_veckan: "Flera gånger i veckan",
  dagligen: "Dagligen",
  storre_delen_av_dagen: "Större delen av dagen",
};
const IMPACT: Record<string, string> = {
  inte_alls: "Inte alls",
  lite: "Lite",
  mattligt: "Måttligt",
  mycket: "Mycket",
  valdigt_mycket: "Väldigt mycket",
};

const DASH = "—";
const n = (v: number | null, unit = "") =>
  v === null ? DASH : `${formatSvNumber(v)}${unit ? ` ${unit}` : ""}`;
const label = (map: Record<string, string>, v: string | null) =>
  v === null ? DASH : (map[v] ?? v);
const joined = (...parts: (string | null)[]) =>
  parts.filter((p): p is string => !!p).join(", ");

/** "WHtR 0,51 = 86 cm / 168 cm" — or why it cannot be computed. */
export function whtrHeadline(m: LowBmiMeasures): string {
  if (m.whtr === null) {
    return m.waistCm === null
      ? "WHtR — (midjemått saknas)"
      : "WHtR — (längd saknas)";
  }
  return `WHtR ${formatWhtr(m.whtr)} = ${n(m.waistCm, "cm")} / ${n(m.heightCm, "cm")}`;
}

export function lowBmiLines(
  m: LowBmiMeasures,
  intake: LowBmiAssessment["intake"],
): Line[] {
  const source = intake ? `F-02 v${intake.templateVersion}` : "F-02";
  const lines: Line[] = [
    {
      label: "Midjemått",
      value: joined(
        n(m.waistCm, "cm"),
        m.waistMeasuredOn ? `mätt ${m.waistMeasuredOn}` : null,
        // TES-1649 — from intake v27 the waist is its own task after F-02.
        m.waistSource === "waist_task"
          ? "patientuppgift i midjemåttsuppgiften"
          : `patientuppgift i ${source}`,
      ),
    },
    {
      label: "Längd",
      value: joined(
        n(m.heightCm, "cm"),
        m.heightSource === "section"
          ? `bekräftad i ${source}`
          : m.heightSource === "intake"
            ? `ur ${source}:s längdfråga`
            : null,
      ),
    },
    // TES-1649 (Ö-1018) — intake v27 no longer asks it; v13–v26 required it.
    ...(m.highestWeightKg === null && m.highestWeightYear === null
      ? []
      : [
          {
            label: "Högsta vuxenvikt",
            value: joined(
              n(m.highestWeightKg, "kg"),
              m.highestWeightYear !== null
                ? `ungefär ${m.highestWeightYear}`
                : null,
              m.earlierBmi !== null
                ? `BMI ${formatSvNumber(m.earlierBmi, 1)}`
                : null,
            ),
          },
        ]),
  ];

  const labValues = [
    m.hba1c !== null ? `HbA1c ${n(m.hba1c, "mmol/mol")}` : null,
    m.triglycerides !== null ? `TG ${n(m.triglycerides, "mmol/L")}` : null,
    m.hdl !== null ? `HDL ${n(m.hdl, "mmol/L")}` : null,
    m.ldl !== null ? `LDL ${n(m.ldl, "mmol/L")}` : null,
    m.tgHdl !== null ? `TG/HDL ${formatTgHdl(m.tgHdl)}` : null,
  ];
  lines.push({
    label: "Tidigare provsvar",
    value:
      m.labStatus !== "ja"
        ? label(LAB_STATUS, m.labStatus)
        : joined(
            labValues.some(Boolean)
              ? joined(...labValues)
              : "inga värden angivna",
            m.labSampledOn ? `provdatum ${m.labSampledOn}` : null,
            m.labProvider ? `vårdgivare: ${m.labProvider}` : null,
          ),
  });

  const bia = [
    m.bodyFatPct !== null ? `kroppsfett ${n(m.bodyFatPct, "%")}` : null,
    m.visceralFat !== null ? `visceralt fett ${n(m.visceralFat)}` : null,
  ];
  lines.push({
    label: "BIA",
    value: bia.some(Boolean)
      ? joined(
          ...bia,
          m.biaMeasuredOn ? `mätt ${m.biaMeasuredOn}` : null,
          m.biaSource ? `källa: ${m.biaSource}` : null,
        )
      : "Ej angiven",
  });

  lines.push(
    {
      label: "Ärftlighet",
      value:
        m.heredity.length > 0
          ? m.heredity.map((v) => label(HEREDITY, v)).join("; ")
          : DASH,
    },
    { label: "Belastningssmärta", value: label(YES_NO_UNSURE, m.loadPain) },
    {
      label: "Food noise",
      value: joined(
        `tankar på mat/vikt: ${label(FREQUENCY, m.foodNoiseFrequency).toLowerCase()}`,
        `påverkan: ${label(IMPACT, m.foodNoiseImpact).toLowerCase()}`,
        `kvarstår trots rutiner: ${label(YES_NO_UNSURE, m.foodNoiseDespiteRoutines).toLowerCase()}`,
      ),
    },
  );
  if (m.foodNoiseNote) {
    lines.push({ label: "Patientens egna ord", value: `”${m.foodNoiseNote}”` });
  }
  return lines;
}

/**
 * Ö-346c — PMOS, hypertension or fatty liver that does not count as an
 * indicator, grouped as the doctor reads it: an earlier diagnosis is
 * history, an uncertain one is something to clarify in the meeting. Empty
 * groups are left out.
 */
export function conditionNoteLines(notes: LowBmiConditionNote[]): Line[] {
  const names = (status: LowBmiConditionNote["status"]) =>
    notes.filter((c) => c.status === status).map((c) => c.name);
  const lines: Line[] = [];
  const earlier = names("tidigare");
  if (earlier.length > 0) {
    lines.push({
      label: "Sjukdomshistorik",
      value: `${earlier.join(", ")} – tidigare diagnos, räknas inte som indikator`,
    });
  }
  const unsure = names("osaker");
  if (unsure.length > 0) {
    lines.push({
      label: "Behöver klargöras",
      value: `${unsure.join(", ")} – osäker diagnos, räknas inte som indikator`,
    });
  }
  return lines;
}
